import React, { useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import {
    makeStyles,
    Card,
    CardContent,
    Typography,
    Avatar,
    Divider,
    Grid,
    Chip,
    Box,
    createTheme,
    ThemeProvider
} from "@material-ui/core";
import {
    EmailOutlined,
    PhoneOutlined,
    LocationOnOutlined,
    AccountCircleOutlined,
    VerifiedUserOutlined,
    ErrorOutline
} from "@material-ui/icons";
import { findCustomersById } from "actions/customers";

// Create a local theme to inject your brand color
const theme = createTheme({
    palette: {
        primary: {
            main: "#d70001",
        },
    },
});

const useStyles = makeStyles((theme) => ({
    root: {
        maxWidth: 850,
        margin: "15px auto",
        borderRadius: 16,
        overflow: "hidden",
        boxShadow: "0 10px 30px rgba(0,0,0,0.05)",
        border: "1px solid #eee",
    },
    headerSection: {
        padding: theme.spacing(4),
        background: "linear-gradient(45deg, #fdfdfd 30%, #fff5f5 90%)",
        display: "flex",
        alignItems: "center",
        [theme.breakpoints.down("xs")]: {
            flexDirection: "column",
            textAlign: "center",
        },
    },
    avatar: {
        width: 100,
        height: 100,
        marginRight: theme.spacing(2),
        fontSize: 32,
        fontWeight: "bold",
        backgroundColor: "#d70001",
        boxShadow: "0 4px 12px rgba(215, 0, 1, 0.2)",
        [theme.breakpoints.down("xs")]: {
            marginRight: 0,
            marginBottom: theme.spacing(2),
        },
    },
    chip: {
        marginRight: theme.spacing(1),
        marginTop: theme.spacing(1),
        fontWeight: 600,
        fontSize: '0.7rem',
    },
    contentArea: {
        padding: theme.spacing(2),
    },
    sectionTitle: {
        display: "flex",
        alignItems: "center",
        marginBottom: theme.spacing(2),
        color: "#d70001",
        fontWeight: 700,
        textTransform: "uppercase",
        letterSpacing: "1px",
        fontSize: "0.85rem",
    },
    infoBox: {
        padding: theme.spacing(2),
        borderRadius: 8,
        backgroundColor: "#f9f9f9",
        height: "100%",
    },
    label: {
        fontSize: 11,
        color: "#888",
        textTransform: "uppercase",
        fontWeight: 600,
    },
    value: {
        fontSize: 14,
        fontWeight: 500,
        color: "#333",
        wordBreak: "break-all"
    },
    iconInline: {
        fontSize: 18,
        marginRight: theme.spacing(1),
    }
}));

const getValue = (val) => (val && val !== "" ? val : "Not Provided");

const getInitials = (name) => {
    if (!name) return "?";
    const parts = name.trim().split(" ");
    if (parts.length === 1) return parts[0][0].toUpperCase();
    return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
};

const CustomerInfo = ({ customerId }) => {
    const classes = useStyles();
    const dispatch = useDispatch();

    const user = useSelector(state => state.customers.customerDetails);
    // Note: Assuming findCustomersById is imported elsewhere
    useEffect(() => {
        if (customerId) {
            dispatch(findCustomersById(customerId));
        }
    }, [customerId, dispatch]);

    if (!user) return null;

    return (
        <ThemeProvider theme={theme}>
            <Card className={classes.root} elevation={0}>
                {/* Visual Header */}
                <Box className={classes.headerSection}>
                    <Avatar src={user.imageUrl || ""} className={classes.avatar}>
                        {getInitials(user.fullName)}
                    </Avatar>
                    <Box>
                        <Typography variant="h5" style={{ fontWeight: 800, color: "#222" }}>
                            {user.fullName || "Unnamed User"}
                        </Typography>

                        <Box display="flex" flexWrap="wrap">
                            <Chip
                                label={user.userType || "GUEST"}
                                variant="outlined"
                                size="small"
                                className={classes.chip}
                            />
                            <Chip
                                label={user.isDisabled ? "Disabled" : "Active Account"}
                                color={user.isDisabled ? "secondary" : "primary"}
                                size="small"
                                className={classes.chip}
                            />
                            {/*user.isPhoneVerified ? (
                                <Chip 
                                    icon={<VerifiedUserOutlined style={{color: '#4caf50', fontSize: 16}} />} 
                                    label="Verified" 
                                    size="small" 
                                    variant="outlined"
                                    className={classes.chip} 
                                    style={{borderColor: '#4caf50', color: '#4caf50'}}
                                />
                            ) : (
                                <Chip 
                                    icon={<ErrorOutline style={{fontSize: 16}} />}
                                    label="Unverified" 
                                    size="small" 
                                    className={classes.chip} 
                                />
                            )*/}
                        </Box>
                    </Box>
                </Box>

                <CardContent className={classes.contentArea}>
                    {/* Contact & Basics */}
                    <Typography className={classes.sectionTitle}>
                        <EmailOutlined className={classes.iconInline} /> Contact Details
                    </Typography>
                    <Grid container spacing={3}>
                        <Grid item xs={12} sm={6}>
                            <Box className={classes.infoBox}>
                                <Typography className={classes.label}>Email Address</Typography>
                                <Typography className={classes.value}>{getValue(user.email)}</Typography>
                            </Box>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <Box className={classes.infoBox}>
                                <Typography className={classes.label}>Phone Number</Typography>
                                <Typography className={classes.value}>{getValue(user.telephone)}</Typography>
                            </Box>
                        </Grid>
                    </Grid>
                    {user.address && (
                        <Box mt={4}>
                            <Typography className={classes.sectionTitle}>
                                <LocationOnOutlined className={classes.iconInline} /> Address Information
                            </Typography>
                            <Grid container spacing={2}>
                                <Grid item xs={12}>
                                    <Box className={classes.infoBox}>
                                        <Typography className={classes.label}>Full Address</Typography>
                                        <Typography className={classes.value}>
                                            {user.address?.fullAddress || "No address on file"}
                                        </Typography>
                                    </Box>
                                </Grid>
                                {['City', 'District', 'State', 'Post Code'].map((field) => (
                                    <Grid item xs={6} sm={3} key={field}>
                                        <Typography className={classes.label}>{field}</Typography>
                                        <Typography className={classes.value}>
                                            {getValue(user.address?.[field.toLowerCase().replace(" ", "")])}
                                        </Typography>
                                    </Grid>
                                ))}
                            </Grid>
                        </Box>
                    )}

                    <Box mt={4}>
                        <Divider style={{ marginBottom: 24 }} />
                        <Typography className={classes.sectionTitle}>
                            <AccountCircleOutlined className={classes.iconInline} /> System Metadata
                        </Typography>
                        <Grid container spacing={2}>
                            <Grid item xs={12} sm={4}>
                                <Typography className={classes.label}>Internal ID</Typography>
                                <Typography variant="caption" className={classes.value} style={{ fontFamily: 'monospace' }}>
                                    {user.id}
                                </Typography>
                            </Grid>
                        </Grid>
                    </Box>
                </CardContent>
            </Card>
        </ThemeProvider>
    );
};

export default CustomerInfo;